import { type ThunkAction, type UnknownAction } from "@reduxjs/toolkit";

import type { RootState } from "@/app/store";
import { ClientsService } from "@/modules/dashboard/clients/services/clientsService";
import { PaymentsWalletsService } from "@/modules/dashboard/wallets/services/paymentsWallets.service";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { UserType } from "@/shared/types/user.entity";
import { readApiCode } from "@/shared/utils/apiError";
import { trimTrailingZeros } from "@/shared/utils/decimal";

import {
  CURRENCY_BY_RUNTIME_CORRIDOR,
  PAYOUT_WIZARD_COUNTRIES,
  resolveRuntimeCorridor,
} from "../constants/dispersions.constants";
import { PaymentsQuotesService, type NativeQuote } from "../services/paymentsQuotes.service";
import type { DispersionQuoteParams } from "../types/dispersions-api.types";
import {
  setCountries,
  setRuntimeWalletLoading,
  setRuntimeWallet,
  setRuntimeWalletError,
  setRuntimeWalletList,
  setRuntimeWalletListLoading,
  setRuntimeWalletsAccessDenied,
  setClientKycLoading,
  setVerifiedClientIds,
  setClientKycError,
  setQuoteLoading,
  startQuoteRequest,
  setQuoteData,
  setQuoteError,
  setQuoteErrorCode,
  updateFormField,
  setLastChangedAmountField,
} from "./dispersionsSlice";

/**
 * Seed the wizard's destination catalog. No HTTP involved anymore: the corridors the runtime executes are a
 * fixed product decision (`PAYOUT_WIZARD_COUNTRIES`), so the selector can never come up empty because a
 * catalog request failed. Kept as a thunk so the fetch-on-mount consumers stay unchanged.
 */
export const thunkFetchCountries =
  (): ThunkAction<void, RootState, unknown, UnknownAction> => (dispatch, getState) => {
    // Idempotent: every wizard consumer dispatches this on mount.
    if (getState().dispersions.countries.countries.length > 0) return;
    dispatch(setCountries(PAYOUT_WIZARD_COUNTRIES));
  };

/**
 * Fetch the NEW payments-runtime (ledger) wallet for a currency — the wallet the BRA/MEX payouts actually
 * debit. Org members are pinned to their own organization server-side; the list is filtered by `currency` on
 * the backend, so this is a single lightweight request. `wallet: null` = the org has not activated a wallet
 * in that currency yet (the payout would fail with insufficient funds on a zero balance).
 */
export const thunkFetchRuntimeWallet =
  (currency: string): ThunkAction<void, RootState, unknown, UnknownAction> =>
  async (dispatch) => {
    dispatch(setRuntimeWalletLoading({ currency, isLoading: true }));
    try {
      const { data } = await PaymentsWalletsService.list({ currency, limit: 1 });
      dispatch(setRuntimeWallet({ currency, wallet: data.data[0] ?? null }));
    } catch (error: unknown) {
      dispatch(
        setRuntimeWalletError({
          currency,
          error: HttpApiService.getErrorMessage(error) || "Error al cargar el saldo de la billetera",
        }),
      );
    }
  };

const RUNTIME_WALLETS_CACHE_MS = 5 * 60 * 1000;
const RUNTIME_WALLETS_PAGE_LIMIT = 200;

/**
 * Fetch every activated wallet at once — the options for the origin selector.
 *
 * The origin is a wallet, not a country, so the list of wallets IS the list of options. Cached for five
 * minutes and single-flighted (mirroring the swaps module): step 1 already blocks on the countries request,
 * and a second blocking fetch on every mount would make the wizard feel slower for data that cannot have
 * changed. `thunkFetchRuntimeWallet` stays for the explicit "refresh balance" button, which must bypass the
 * cache for one currency.
 *
 * INTERNAL staff have no organization of their own, so their wallet list comes back empty (or errors); the
 * selector falls back to `availableCurrencies` for them and skips the local balance check. The two requests
 * are therefore settled INDEPENDENTLY: with `Promise.all`, a wallet-list failure took the currency catalog
 * down with it and left the selector with no options at all.
 */
export const thunkFetchWalletList =
  (force = false): ThunkAction<Promise<void>, RootState, unknown, UnknownAction> =>
  async (dispatch, getState) => {
    const { runtimeWallets } = getState().dispersions;
    if (runtimeWallets.isListLoading || !runtimeWallets.hasAccess) return;
    const isFresh =
      runtimeWallets.lastFetched !== null &&
      Date.now() - runtimeWallets.lastFetched < RUNTIME_WALLETS_CACHE_MS;
    if (!force && isFresh) return;

    dispatch(setRuntimeWalletListLoading(true));
    const state = getState();
    const user = state.auth.user;
    const organizationId = user?.organization_id ?? user?.organization?.id;

    const [walletsResult, currenciesResult] = await Promise.allSettled([
      PaymentsWalletsService.list({
        limit: RUNTIME_WALLETS_PAGE_LIMIT,
        // Members are pinned server-side; only INTERNAL may name an org.
        ...(user?.user_type === UserType.INTERNAL && organizationId ? { organizationId } : {}),
      }),
      PaymentsWalletsService.listCurrencies(),
    ]);

    // A missing `payments:read` grant latches for the session: each denial costs a
    // silent /auth/refresh + replay, so retrying on every mount is expensive. Only
    // latch when BOTH calls were denied — one of them failing is not a permission
    // problem.
    const denied = [walletsResult, currenciesResult].every(
      (r) => r.status === "rejected" && HttpApiService.isAccessDeniedError(r.reason),
    );
    if (denied) {
      dispatch(setRuntimeWalletsAccessDenied());
      return;
    }

    if (walletsResult.status === "rejected" && currenciesResult.status === "rejected") {
      dispatch(setRuntimeWalletListLoading(false));
      return;
    }

    dispatch(
      setRuntimeWalletList({
        wallets: walletsResult.status === "fulfilled" ? walletsResult.value.data.data : [],
        // Omitted on failure so a partial outcome cannot wipe a catalog already in
        // the store.
        ...(currenciesResult.status === "fulfilled"
          ? { availableCurrencies: currenciesResult.value.data.currencies }
          : {}),
      }),
    );
  };

/**
 * Fetch the org's KYC-VERIFIED client ids from the organization detail (the paginated clients list carries no
 * KYC data). Cached: runs at most once per session unless `force` — the recipient panels of every corridor
 * and the step-2 gate all read the same slice, so switching corridors/steps never refetches. A failed fetch
 * keeps `verifiedClientIds` null, so any later call retries automatically. This list is the ONLY KYC
 * enforcement of the dispersal flow (the backend does not re-check), so errors are stored and the gate fails
 * CLOSED; only INTERNAL users (no single org — the fetch never runs) keep fail-open by design.
 */
export const thunkFetchVerifiedClientIds =
  (force = false): ThunkAction<void, RootState, unknown, UnknownAction> =>
  async (dispatch, getState) => {
    const { clientKyc } = getState().dispersions;
    if (clientKyc.isLoading || (!force && clientKyc.verifiedClientIds !== null)) return;

    const user = getState().auth.user;
    const organizationId = user?.organization_id ?? user?.organization?.id;
    if (!organizationId) return;

    dispatch(setClientKycLoading(true));
    try {
      const { data } = await ClientsService.fetchOrganizationById(organizationId);
      dispatch(
        setVerifiedClientIds(data.clients.filter((c) => c.kyc_status === "VERIFIED").map((c) => c.id)),
      );
    } catch (error: unknown) {
      // Stored error → the recipient panels surface a retry alert and the step-2 gate fails CLOSED.
      dispatch(
        setClientKycError(
          HttpApiService.getErrorMessage(error) || "No fue posible verificar el estado KYC de los clientes",
        ),
      );
    }
  };

/**
 * Currency for a destination/origin spelling (ISO3 code or country name), from the countries already in the
 * store. The native quote engine is priced per currency; only the legacy PHP proxy speaks country codes.
 *
 * Resolves to whether a FRESH quote is now in the store. It never rejects — a failed quote is state, not an
 * exception, so the form can show a retry instead of a crash — which is exactly why the boolean exists:
 * awaiting alone tells a caller nothing, and the re-quote modal used to close itself on failure because of
 * it. `false` also covers a superseded request: a newer one is still running and will report for itself.
 */
export const thunkFetchQuote =
  (params: DispersionQuoteParams): ThunkAction<Promise<boolean>, RootState, unknown, UnknownAction> =>
  async (dispatch, getState) => {
    dispatch(startQuoteRequest());

    // Claim this request's place in line and drop the response if a later one has since started. Two can be
    // in flight at once — the step-1 debounce fires on every keystroke, and the step-2 Colombia rail toggle
    // re-quotes with no debounce at all — so the slower one could otherwise land last and install a price
    // for the corridor the user just left, taking the mirrored amount field with it.
    const seq = getState().dispersions.quote.requestSeq;
    const isStale = () => getState().dispersions.quote.requestSeq !== seq;

    // Which side the user typed decides which side the response fills back in.
    const editedSide = params.sendAmount !== undefined ? "origin" : "destination";
    dispatch(setLastChangedAmountField(editedSide));

    try {
      const quote = await fetchNativeQuote(params, getState());
      if (isStale()) return false;
      dispatch(setQuoteData(quote));

      // Mirror the other field from the response, keeping the decimal string the
      // API returned — a Number round-trip here would lose 8-dp precision.
      dispatch(
        updateFormField(
          editedSide === "origin"
            ? {
                field: "amount_destination",
                // What the beneficiary receives, which is what the field means.
                value: trimTrailingZeros(quote.amounts.payout_net ?? quote.amounts.receive),
              }
            : { field: "amount_origin", value: trimTrailingZeros(quote.amounts.send) },
        ),
      );
      return true;
    } catch (error: unknown) {
      if (isStale()) return false;
      dispatch(setQuoteError(HttpApiService.getErrorMessage(error)));
      dispatch(setQuoteErrorCode(readApiCode(error)));
      return false;
    } finally {
      // A superseded request must not clear the flag: the newer one is still loading.
      if (!isStale()) dispatch(setQuoteLoading(false));
    }
  };

/**
 * Prices a dispersion on the native FX engine.
 *
 * `country` + `rail` are always sent: they freeze the dispersion commission next to the FX price, and a
 * payout can only redeem a quote that carries them. The legacy PHP branch is gone — the destination selector
 * only offers runtime corridors, so it was unreachable.
 */
async function fetchNativeQuote(params: DispersionQuoteParams, state: RootState): Promise<NativeQuote> {
  const corridor = resolveRuntimeCorridor(params.destinationCountry);
  if (!corridor) throw new Error("unsupported_corridor");

  const user = state.auth.user;
  const organizationId = user?.organization_id ?? user?.organization?.id;

  const { data } = await PaymentsQuotesService.createQuote({
    origin_currency: params.originCurrency,
    // The corridor is the source of truth for the delivery currency, so the quote
    // does not depend on the countries catalog having loaded.
    destination_currency: CURRENCY_BY_RUNTIME_CORRIDOR[corridor],
    service: "payout",
    country: corridor,
    rail: params.rail,
    // Money as decimal strings — the engine rejects anything a currency cannot
    // actually settle (COP has no cents), instead of silently rounding it.
    ...(params.sendAmount !== undefined ? { send_amount: params.sendAmount } : {}),
    ...(params.receiveAmount !== undefined ? { receive_amount: params.receiveAmount } : {}),
    // Members are pinned server-side and must not send it; INTERNAL users must.
    ...(user?.user_type === UserType.INTERNAL && organizationId ? { organization_id: organizationId } : {}),
  });

  return data.quote;
}
